import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useSubscriptionsOverview,
  type SubscriptionOverviewFilters,
  type SubscriptionOverview,
} from "../api/hooks";
import { DataTable, createColumnHelper } from "../components/DataTable";
import { Button, Chip, ErrorNotice, Field, Input, PageHeader, Select } from "../components/ui";

/**
 * Vista global de suscripciones para el super-admin (CLAUDE.md Fase 7, paso 9).
 *
 * Muestra el estado de suscripción de todos los tenants con filtros por
 * `status` y búsqueda libre de texto (nombre/CIF).
 * La columna "Acciones" permite ir directamente al modal Gestionar del tenant
 * (ruta /empresas con el tenantId como hash).
 */

const SUBSCRIPTION_STATUSES = [
  { value: "", label: "Todos los estados" },
  { value: "TRIAL", label: "Prueba" },
  { value: "ACTIVE", label: "Activa" },
  { value: "PENDING_PAYMENT", label: "Pendiente pago" },
  { value: "BLOCKED", label: "Bloqueada" },
  { value: "CANCELLED", label: "Cancelada" },
  { value: "SIN_PLAN", label: "Sin plan" },
];

const STATUS_TONE: Record<string, "ok" | "warn" | "danger" | "neutral"> = {
  TRIAL: "neutral",
  ACTIVE: "ok",
  PENDING_PAYMENT: "warn",
  BLOCKED: "danger",
  CANCELLED: "neutral",
  SIN_PLAN: "neutral",
};

const STATUS_LABELS: Record<string, string> = {
  TRIAL: "Prueba",
  ACTIVE: "Activa",
  PENDING_PAYMENT: "Pago pendiente",
  BLOCKED: "Bloqueada",
  CANCELLED: "Cancelada",
  SIN_PLAN: "Sin plan",
};

const PERIODICITY_LABELS: Record<string, string> = {
  MONTHLY: "Mensual",
  QUARTERLY: "Trimestral",
  ANNUAL: "Anual",
};

const column = createColumnHelper<SubscriptionOverview>();

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("es-ES");
}

/** Días naturales que quedan hasta `dateStr` (negativo = ya expiró). */
function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export function SubscriptionsPage() {
  const [filters, setFilters] = useState<SubscriptionOverviewFilters>({ status: "", search: "" });
  const [appliedFilters, setAppliedFilters] = useState<SubscriptionOverviewFilters>({});
  const navigate = useNavigate();

  const query = useSubscriptionsOverview(appliedFilters);

  const applyFilters = (): void => {
    setAppliedFilters({
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.search?.trim() ? { search: filters.search.trim() } : {}),
    });
  };

  const goToManage = (tenantId: string): void => {
    // Navega a /empresas con el tenantId como hash para que TenantsPage
    // abra directamente el modal de Gestionar de ese tenant.
    void navigate(`/empresas#${tenantId}`);
  };

  const columns = [
    column.accessor("tenantName", {
      header: "Tenant",
      cell: (info) => (
        <div>
          <p className="font-medium">{info.getValue()}</p>
          {info.row.original.tenantCif ? (
            <p className="ident text-xs text-ink-500 dark:text-ink-400">
              {info.row.original.tenantCif}
            </p>
          ) : null}
        </div>
      ),
    }),
    column.accessor("billingPlanName", {
      header: "Plan",
      cell: (info) => info.getValue() ?? <span className="text-ink-400 dark:text-ink-500">—</span>,
    }),
    column.accessor("periodicity", {
      header: "Periodicidad",
      cell: (info) => {
        const v = info.getValue();
        if (!v) return <span className="text-ink-400 dark:text-ink-500">—</span>;
        return <span className="ident text-xs">{PERIODICITY_LABELS[v] ?? v}</span>;
      },
    }),
    column.accessor("status", {
      header: "Estado",
      cell: (info) => {
        const status = info.getValue();
        const tone = STATUS_TONE[status] ?? "neutral";
        const label = STATUS_LABELS[status] ?? status;

        if (status === "TRIAL") {
          const days = daysUntil(info.row.original.trialEndsAt);
          const daysLabel =
            days === null
              ? null
              : days > 0
                ? `${days} día${days === 1 ? "" : "s"} restante${days === 1 ? "" : "s"}`
                : days === 0
                  ? "Expira hoy"
                  : "Expirado";
          // Color del contador según urgencia
          const daysClass =
            days === null
              ? ""
              : days > 5
                ? "text-ink-500 dark:text-ink-400"
                : days > 0
                  ? "text-amber-700 dark:text-amber-400 font-medium"
                  : "text-signal-danger font-medium";
          return (
            <div className="flex flex-col gap-1">
              <Chip tone={tone}>{label}</Chip>
              {daysLabel ? (
                <span className={`text-xs ${daysClass}`}>{daysLabel}</span>
              ) : null}
            </div>
          );
        }

        return <Chip tone={tone}>{label}</Chip>;
      },
    }),
    column.accessor("currentPeriodEnd", {
      header: "Fin período",
      cell: (info) => <span className="num">{formatDate(info.getValue())}</span>,
    }),
    column.accessor("hasPaymentMethodOnFile", {
      header: "Método pago",
      cell: (info) => {
        const v = info.getValue();
        if (v === null || v === undefined) return <span className="text-ink-400 dark:text-ink-500">—</span>;
        return v ? <Chip tone="ok">Sí</Chip> : <Chip tone="neutral">No</Chip>;
      },
    }),
    column.accessor("autoRenew", {
      header: "Auto-renew",
      cell: (info) => {
        const v = info.getValue();
        if (v === null || v === undefined) return <span className="text-ink-400 dark:text-ink-500">—</span>;
        return v ? <Chip tone="ok">Sí</Chip> : <Chip tone="neutral">No</Chip>;
      },
    }),
    column.display({
      id: "actions",
      header: "",
      cell: (info) => {
        const row = info.row.original;
        const hasIssue = row.status === "PENDING_PAYMENT" || row.status === "BLOCKED";
        return (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="quiet"
              onClick={() => goToManage(row.tenantId)}
              title="Abrir modal de gestión del tenant"
            >
              Gestionar
            </Button>
            {hasIssue ? (
              <Button
                variant="quiet"
                onClick={() => goToManage(row.tenantId)}
                title="Ir al tenant para gestionar el problema de pago"
                className="text-amber-700 dark:text-amber-400"
              >
                Ver pago
              </Button>
            ) : null}
          </div>
        );
      },
    }),
  ];

  return (
    <>
      <PageHeader
        eyebrow="Facturación"
        title="Suscripciones"
        description="Estado de suscripción de todos los tenants. Filtra por estado o busca por nombre/CIF."
      />

      {/* Filtros */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Estado" htmlFor="sub-status">
          <Select
            id="sub-status"
            value={filters.status ?? ""}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            {SUBSCRIPTION_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Buscar tenant" htmlFor="sub-search">
          <Input
            id="sub-search"
            value={filters.search ?? ""}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder="Nombre o CIF"
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilters();
            }}
          />
        </Field>
        <Button variant="primary" onClick={applyFilters}>
          Filtrar
        </Button>
        <Button
          onClick={() => {
            setFilters({ status: "", search: "" });
            setAppliedFilters({});
          }}
        >
          Limpiar
        </Button>
      </div>

      {query.isError ? (
        <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={query.data ?? []}
          isLoading={query.isLoading}
          emptyMessage="No hay suscripciones que coincidan con los filtros."
        />
      )}
    </>
  );
}
